import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { DateInput, fmtDate } from "@/components/DateInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, Pencil, Repeat, CalendarClock } from "lucide-react";

const COLUMNS = [
  { k: "todo", l: "To Do", cls: "border-slate-300" },
  { k: "in_progress", l: "Dikerjakan", cls: "border-blue-400" },
  { k: "review", l: "Review", cls: "border-amber-400" },
  { k: "done", l: "Selesai", cls: "border-emerald-400" },
];
const PRIORITY = { low: { l: "Rendah", cls: "bg-slate-200 text-slate-700" }, normal: { l: "Normal", cls: "bg-blue-100 text-blue-700" }, high: { l: "Tinggi", cls: "bg-rose-100 text-rose-700" } };
const RECUR = [
  { k: "none", l: "Sekali" }, { k: "daily", l: "Harian" }, { k: "weekly", l: "Mingguan" },
  { k: "monthly", l: "Bulanan" }, { k: "quarterly", l: "Triwulan" },
];

export default function ProjectTracker() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canEdit = ["super_admin", "supervisor"].includes(user?.role);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [fc, setFc] = useState("all");
  const [fa, setFa] = useState("all");

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (fc !== "all") p.set("client_id", fc);
    if (fa !== "all") p.set("assignee_id", fa);
    const { data } = await api.get(`/tasks?${p.toString()}`);
    setTasks(data);
  }, [fc, fa]);
  useEffect(() => {
    Promise.all([api.get("/clients?include_inactive=1"), api.get("/internal-users")]).then(([c, u]) => { setClients(c.data); setUsers(u.data); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "";
  const userName = (id) => users.find((u) => u.id === id)?.name || "";

  const move = async (task, status) => {
    try {
      const { data } = await api.post(`/tasks/${task.id}/move`, { status });
      if (data.spawned) toast.success(`Tugas berulang dibuat: ${fmtDate(data.spawned.due_date)}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus tugas ini?")) return; await api.delete(`/tasks/${id}`); toast.success("Dihapus"); load(); };

  return (
    <div className="space-y-4" data-testid="project-tracker-page">
      <div className="page-sticky flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight flex items-center gap-2"><ClipboardList className="h-6 w-6 text-primary" /> {t("nav.projects")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Pekerjaan rutin & proyek klien — harian, bulanan, triwulan, atau sekali.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={fc} onValueChange={setFc}>
            <SelectTrigger className="w-44 h-9" data-testid="pt-filter-client"><SelectValue placeholder="Semua klien" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Klien</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fa} onValueChange={setFa}>
            <SelectTrigger className="w-44 h-9" data-testid="pt-filter-assignee"><SelectValue placeholder="Semua PIC" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua PIC</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          {canEdit && <TaskForm clients={clients} users={users} onDone={load} />}
        </div>
      </div>
      {!canEdit && <p className="text-xs text-muted-foreground" data-testid="pt-readonly-note">Mode staff: Anda hanya dapat memperbarui status tugas yang ditugaskan kepada Anda.</p>}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => {
          const items = tasks.filter((x) => x.status === col.k);
          return (
            <div key={col.k} className={`rounded-lg border-t-4 ${col.cls} bg-card border border-border p-3 space-y-3`} data-testid={`pt-col-${col.k}`}>
              <div className="flex items-center justify-between"><span className="font-head font-semibold text-sm">{col.l}</span><Badge variant="secondary" className="text-[10px]">{items.length}</Badge></div>
              {items.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">Kosong</p>}
              {items.map((task) => {
                const pr = PRIORITY[task.priority] || PRIORITY.normal;
                return (
                  <div key={task.id} className="rounded-md border border-border bg-background p-3 space-y-2 shadow-sm" data-testid={`pt-task-${task.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-medium leading-tight">{task.title}</span>
                      {canEdit && (
                        <div className="flex gap-0.5 shrink-0">
                          <TaskForm clients={clients} users={users} editTask={task} onDone={load} trigger={<Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`pt-edit-${task.id}`}><Pencil className="h-3.5 w-3.5" /></Button>} />
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => del(task.id)} data-testid={`pt-del-${task.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </div>
                    {task.description && <p className="text-xs text-muted-foreground line-clamp-2">{task.description}</p>}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge className={`text-[9px] ${pr.cls}`}>{pr.l}</Badge>
                      {task.client_id && <Badge variant="outline" className="text-[9px]">{clientName(task.client_id)}</Badge>}
                      {task.recurrence && task.recurrence !== "none" && <Badge variant="outline" className="text-[9px] gap-1"><Repeat className="h-2.5 w-2.5" />{RECUR.find((r) => r.k === task.recurrence)?.l}</Badge>}
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1">{task.due_date && <><CalendarClock className="h-3 w-3" />{fmtDate(task.due_date)}</>}</span>
                      <span>{userName(task.assignee_id)}</span>
                    </div>
                    <Select value={task.status} onValueChange={(v) => move(task, v)} disabled={!canEdit && task.assignee_id !== user?.id}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`pt-status-${task.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{COLUMNS.map((c) => <SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TaskForm({ clients, users, editTask, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const blank = { client_id: "none", title: "", description: "", assignee_id: "none", status: "todo", priority: "normal", due_date: "", recurrence: "none", recurrence_day: "", progress: 0 };
  const [f, setF] = useState(blank);
  const [clientIds, setClientIds] = useState([]);
  useEffect(() => {
    if (!open) return;
    if (editTask) setF({ ...blank, ...editTask, client_id: editTask.client_id || "none", assignee_id: editTask.assignee_id || "none", recurrence_day: editTask.recurrence_day || "" });
    else { setF(blank); setClientIds([]); }
  }, [open, editTask]);

  const save = async () => {
    if (!f.title.trim()) { toast.error("Judul wajib diisi"); return; }
    const common = {
      title: f.title, description: f.description,
      assignee_id: f.assignee_id === "none" ? "" : f.assignee_id, status: f.status, priority: f.priority,
      due_date: f.due_date || "", recurrence: f.recurrence,
      recurrence_day: f.recurrence_day ? Number(f.recurrence_day) : null, progress: Number(f.progress) || 0,
    };
    try {
      if (editTask) {
        await api.put(`/tasks/${editTask.id}`, { ...common, client_id: f.client_id === "none" ? "" : f.client_id });
        toast.success("Tersimpan");
      } else {
        const { data } = await api.post("/tasks/bulk", { ...common, client_ids: clientIds });
        toast.success(data.count > 1 ? `${data.count} tugas dibuat` : "Tersimpan");
      }
      setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const showDay = ["monthly", "quarterly"].includes(f.recurrence);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="pt-add-task"><Plus className="h-4 w-4 mr-2" />Tugas Baru</Button>}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{editTask ? "Edit Tugas" : "Tugas Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Judul</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="pt-title" placeholder="mis. Lapor SPT Masa PPN" /></div>
          <div className="space-y-1.5"><Label>Deskripsi</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="pt-desc" /></div>
          {editTask ? (
            <div className="space-y-1.5"><Label>Klien</Label>
              <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
                <SelectTrigger data-testid="pt-client"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Internal / Umum —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <ClientMultiSelect clients={clients} value={clientIds} onChange={setClientIds} />
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>PIC</Label>
              <Select value={f.assignee_id} onValueChange={(v) => setF({ ...f, assignee_id: v })}>
                <SelectTrigger data-testid="pt-assignee"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Belum ditugaskan —</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Prioritas</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="pt-priority"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="low">Rendah</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">Tinggi</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="pt-status-form"><SelectValue /></SelectTrigger>
                <SelectContent>{COLUMNS.map((c) => <SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Tenggat</Label><DateInput value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="pt-due" /></div>
            <div className="space-y-1.5"><Label>Perulangan</Label>
              <Select value={f.recurrence} onValueChange={(v) => setF({ ...f, recurrence: v })}>
                <SelectTrigger data-testid="pt-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>{RECUR.map((r) => <SelectItem key={r.k} value={r.k}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {showDay && <div className="space-y-1.5"><Label>Tanggal (hari ke-)</Label><Input type="number" min="1" max="31" value={f.recurrence_day} onChange={(e) => setF({ ...f, recurrence_day: e.target.value })} data-testid="pt-recur-day" placeholder="mis. 10" /></div>}
            <div className="space-y-1.5"><Label>Progres (%)</Label><Input type="number" min="0" max="100" value={f.progress} onChange={(e) => setF({ ...f, progress: e.target.value })} data-testid="pt-progress" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="pt-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientMultiSelect({ clients, value, onChange }) {
  const [q, setQ] = useState("");
  const filtered = clients.filter((c) => !q || `${c.name} ${c.code}`.toLowerCase().includes(q.toLowerCase()));
  const allSelected = filtered.length > 0 && filtered.every((c) => value.includes(c.id));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const toggleAll = () => {
    if (allSelected) onChange(value.filter((id) => !filtered.some((c) => c.id === id)));
    else onChange(Array.from(new Set([...value, ...filtered.map((c) => c.id)])));
  };
  return (
    <div className="space-y-1.5" data-testid="pt-client-multi">
      <div className="flex items-center justify-between">
        <Label>Klien (bisa pilih banyak)</Label>
        <span className="text-[11px] text-muted-foreground" data-testid="pt-client-count">{value.length} terpilih</span>
      </div>
      <Input placeholder="Cari klien..." value={q} onChange={(e) => setQ(e.target.value)} className="h-8" data-testid="pt-client-search" />
      <div className="max-h-44 overflow-auto rounded-md border border-border divide-y">
        <label className="flex items-center gap-2 px-2.5 py-2 cursor-pointer bg-secondary/40" data-testid="pt-client-all">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
          <span className="text-sm font-medium">Pilih Semua{q ? " (hasil pencarian)" : ""}</span>
        </label>
        {filtered.map((c) => (
          <label key={c.id} className="flex items-center gap-2 px-2.5 py-2 cursor-pointer hover:bg-secondary/40" data-testid={`pt-client-opt-${c.code}`}>
            <Checkbox checked={value.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
            <span className="text-sm truncate">{c.name}</span>
            <span className="text-[11px] text-muted-foreground ml-auto shrink-0">{c.code}</span>
          </label>
        ))}
        {filtered.length === 0 && <p className="text-xs text-muted-foreground p-3 text-center">Tidak ada klien.</p>}
      </div>
      <p className="text-[11px] text-muted-foreground">Kosongkan untuk tugas internal/umum. Memilih beberapa klien membuat satu kartu tugas terpisah untuk masing-masing klien.</p>
    </div>
  );
}
